<script setup lang="ts">
import EmptyState from '@/components/EmptyState.vue'
import ContestStatus from '@/views/contests/Components/ContestStatus.vue'

import type { ContestGame } from '@/types/types'
import { getRowContentCreator, getRowContentScope } from '@/utils/contentScope'

const { copyToClipboard } = useDOM()
const props = withDefaults(
  defineProps<{
    contests: ContestGame[]
    total: number
    currentPage: number
    lastPage: number
    pageSize: number
    hasNextPage: boolean
    hasPrevPage: boolean
    isFetching: boolean
    showEmptyState?: boolean
    emptyStateMessage?: string
    tableTitle?: string | null
    withOrigin?: boolean
  }>(),
  {
    showEmptyState: false,
    emptyStateMessage: '',
    withOrigin: false
  }
)

const router = useRouter()
const { t } = useI18n()
const { formatNumber } = useNumber()

const resolvedTableTitle = computed(() =>
  props.tableTitle === undefined ? t('pages.contests.contestsPage.title') : props.tableTitle
)

const boosts = {
  none: {
    icon: 'HeroUserGroupIconSolid',
    title: t('pages.autopilot.autopilotsPage.boost.none')
  },
  facebook: {
    icon: 'IconoirFacebook',
    color: 'icon-facebook',
    title: t('pages.autopilot.autopilotsPage.boost.facebook')
  },
  instagram: {
    icon: 'IconoirInstagram',
    color: 'icon-instagram',
    title: t('pages.autopilot.autopilotsPage.boost.instagram')
  },
  google: {
    icon: 'IconoirGoogleCircle',
    color: 'icon-google',
    title: t('pages.autopilot.autopilotsPage.boost.google')
  },
  custom: {
    icon: 'HeroArrowTopRightOnSquareIconSolid',
    title: t('pages.autopilot.autopilotsPage.boost.custom')
  }
}

const goToDetails = (event: any) => {
  const contest = event.data.contest
  const routeName = contest?.isLegacy ? 'LegacyContestDetail' : 'ContestDetail'
  router.push({ name: routeName, params: { contestId: contest.id } })
}

const table = computed(() => ({
  columns: {
    name: {
      label: t('pages.contests.contestsPage.table.thead.name'),
      headerClass: '!w-full max-w-[1/2]'
    },
    boostSlug: {
      label: t('pages.contests.contestsPage.table.thead.boost'),
      headerClass: '!w-auto'
    },
    status: {
      label: t('pages.contests.contestsPage.table.thead.status'),
      headerClass: '!w-auto'
    },
    ...(props.withOrigin && {
      origin: {
        label: t('contentScope.origin'),
        headerClass: '!w-auto'
      }
    }),
    stores: {
      label: t('pages.contests.contestsPage.table.thead.stores'),
      headerClass: '!w-auto'
    },
    participantsCounts: {
      label: t('pages.contests.contestsPage.table.thead.participantsCounts'),
      headerClass: '!w-auto',
      numeric: true
    },
    ...(props.withOrigin && {
      creator: {
        label: t('contentScope.filters.createdBy'),
        headerClass: '!w-auto'
      }
    }),
    url: {
      label: t('pages.contests.contestsPage.table.thead.url'),
      headerClass: '!w-auto',
      align: 'right' as const
    }
  }
}))

const rows = computed(() => {
  return props.contests.map((contest) => {
    return {
      id: parseInt(contest.id),
      data: {
        name: contest.name,
        contest: contest,
        boostSlug: boosts[contest.contestGameBoostType],
        participantsCounts: formatNumber(contest.participantsCounts),
        stores: contest.stores,
        url: contest.shortLink
      }
    }
  })
})

const pagination = computed(() => ({
  currentPage: props.currentPage,
  lastPage: props.lastPage,
  total: props.total,
  pageSize: props.pageSize,
  hasNextPage: props.hasNextPage,
  hasPrevPage: props.hasPrevPage
}))

const emit = defineEmits(['change-page'])
const getContests = (page: number) => {
  emit('change-page', page)
}
</script>

<template>
  <div class="contest-list">
    <div v-if="isFetching" class="flex justify-center py-8">
      <BaseIcon name="HeroArrowPathIconOutline" class="h-6 w-6 animate-spin text-muted-foreground" />
    </div>

    <div v-else-if="contests.length === 0 && showEmptyState" class="py-8">
      <EmptyState :message="emptyStateMessage || t('pages.contests.contestsPage.emptyState.title')" module="contests" />
    </div>

    <div v-else-if="contests.length > 0">
      <AppTable
        :columns="table.columns"
        :rows="rows"
        :pagination="pagination"
        :is-loading="isFetching"
        row-classes="cursor-pointer"
        :table-title="resolvedTableTitle"
        @update:change-page="getContests"
        @row-clicked="goToDetails"
      >
        <template #td-name="{ data }">
          <div class="flex items-center gap-3">
            <p
              class="max-w-[40ch] truncate text-sm font-medium leading-5 text-foreground"
              :title="data.name || undefined"
            >
              {{ data.name || '-' }}
            </p>
            <BaseChip v-if="data.contest.isLegacy" :text="t('pages.contests.contestsPage.legacyFormat')" />
          </div>
        </template>

        <template #th-origin>
          <ContentScopeColumnHeader subject="contest_game" />
        </template>
        <template #td-origin="{ data }">
          <ContentScopeBadge :scope="getRowContentScope(data.contest)" />
        </template>

        <template #td-creator="{ data }">
          <ContentScopeCreator :creator="getRowContentCreator(data.contest)" />
        </template>

        <template #td-status="{ data }">
          <ContestStatus :contest="data.contest" />
        </template>

        <template #td-boostSlug="{ data }">
          <div v-if="data.boostSlug" class="flex items-center gap-2">
            <BaseIcon
              v-if="data.boostSlug.icon"
              :name="data.boostSlug.icon"
              :class="data.boostSlug.color"
              class="h-5 w-5"
            />
            <img v-if="data.boostSlug.img" :src="data.boostSlug.img" class="h-5 w-5" />
            <span class="text-sm text-foreground">
              {{ data.boostSlug.title }}
            </span>
          </div>
        </template>

        <template #td-stores="{ data }">
          <div class="flex items-center">
            <StoreTooltip :stores="data.stores" />
          </div>
        </template>

        <template #td-url="{ data }">
          <div class="flex items-center justify-end">
            <button
              v-if="data.url"
              type="button"
              class="cursor-pointer flex items-center gap-2 text-sm text-muted-foreground duration-300 hover:text-primary"
              @click.stop="copyToClipboard(data.url)"
            >
              <BaseIcon name="HeroLinkIconSolid" class="h-5 w-5 text-primary" />
            </button>
            <p v-else>-</p>
          </div>
        </template>
      </AppTable>
    </div>
  </div>
</template>

<style scoped>
@reference "@/input.css";

.contest-list {
  @apply w-full;
}
</style>
